import type { FunctionComponent } from 'react'
import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import type { LinksListExpandable } from '@which/seatbelt'
import { ContentCardV2, useId } from '@which/seatbelt'
import type { ImageSource } from '@which/seatbelt/src/types'
import { dynamicGa4DataLayerPush, isExternalLink } from '@which/shared'

import { useFeatureValue } from '@growthbook/growthbook-react'

import type { Link } from '../../../generated/frontend'
import type { PageParams } from '../../../routes'
import styles from './ToolCta.module.scss'

export const ToolCTA: FunctionComponent<Props> = ({
  button,
  description,
  image,
  title,
  widgetImageAspectRatio,
  linksListExpandableData,
  captionText,
}) => {
  const { subVerticalSlug } = useParams<PageParams>()
  const isInsuranceArticle = subVerticalSlug === 'insurance'
  const moneyCTATest = useFeatureValue('whc-873-money-cta-changes', 'Control')
  const hideCard = isInsuranceArticle && moneyCTATest === 'Variation 2'
  const uniqueId = useId()
  const contentHtmlId = useMemo(() => `tool-cta-links-list-${uniqueId}`, [uniqueId])
  const buttonProps: Link = { text: button?.text ?? '', href: button?.href ?? '' }
  const buttonLinkAdditionalProps = isExternalLink(buttonProps.href)
    ? { target: '_blank', rel: 'nofollow noopener noreferrer' }
    : {}

  const imageProps = {
    aspectRatioMain: getImageAspectRatio(widgetImageAspectRatio),
    imageAlt: image?.alt || '',
    imageSources: image?.sources,
    lazyLoad: false,
    str: image?.src || '',
  }

  const tracking = isInsuranceArticle
    ? {
        onClick: () => {
          dynamicGa4DataLayerPush({
            event: 'click_cta',
            utagid: 'WHC873DP01',
            item_group: 'in article cta',
            item_text: linksListExpandableData?.button.text || '',
            item_parent_text: title,
            item_spec: 'in image',
          })
        },
      }
    : {}

  const secondaryLinksProps = {
    ariaLabel: linksListExpandableData?.button.ariaLabel || '',
    buttonText: linksListExpandableData?.button.text || '',
    contentHtmlId,
    links: linksListExpandableData?.items || [],
    ...tracking,
  }

  return hideCard ? null : (
    <div data-testid="tool-cta" className={styles.toolCTA}>
      <ContentCardV2
        arrangement={'horizontal'}
        buttonLinkText={buttonProps.text}
        buttonLinkAdditionalProps={buttonLinkAdditionalProps}
        description={description}
        imageRight={true}
        imgObj={imageProps}
        primaryLink={buttonProps.href}
        secondaryLinks={secondaryLinksProps}
        title={title}
        legalText={captionText}
        titleTag="h3"
      />
    </div>
  )
}

///////// IMPLEMENTATION /////////

const getImageAspectRatio = (widgetImageAspectRatio: WidgetImageAspectRatio) =>
  ({
    ar_1to1_: 'one-to-one' as const,
    ar_2to1_: 'two-to-one' as const,
  })[widgetImageAspectRatio]

type WidgetImageAspectRatio = 'ar_1to1_' | 'ar_2to1_'

export type Props = {
  button: Link
  description?: string
  image?: {
    alt: string
    sources?: ImageSource[]
    src: string
  }
  linksListExpandableData?: Omit<Parameters<typeof LinksListExpandable>[0], 'contentHtmlId'>
  title: string
  widgetImageAspectRatio: WidgetImageAspectRatio
  captionText?: string
}
